import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Bike, CheckCircle2, ClipboardList, PackageCheck, Truck, X } from 'lucide-react';
import StatCard from '../../components/StatCard';
import EmptyState from '../../components/EmptyState';
import OrderDetailsDrawer from '../../components/OrderDetailsDrawer';
import AssignRiderDrawer from '../../components/AssignRiderDrawer';
import Button from '../../components/Button';
import { useAuth } from '../../context/AuthContext';
import { useOrders } from '../../context/OrdersContext';
import {
  ORDER_QUEUE_COLUMNS,
  ORDER_QUEUE_ERRORS,
  ORDER_STATUS,
  assignRider as assignRiderApi,
  cancelOrder as cancelOrderApi,
  confirmOrder as confirmOrderApi,
  getOrdersSummary,
  listOrders,
  listRiders,
  packOrder as packOrderApi,
  // resendBill as resendBillApi,
  sortByUrgency,
  unassignRider as unassignRiderApi,
} from '../../api/orders';
import { statusColors } from '../../theme';
import OrderCard from './OrderCard';
import styles from './OrderQueue.module.css';

const MAX_TRIP_ORDERS = 2;

function friendlyError(err) {
  return ORDER_QUEUE_ERRORS[err.message] || err.message || 'Something went wrong.';
}

export default function OrderQueue() {
  const { user } = useAuth();
  const { notify, refreshQueueBadge } = useOrders();
  const [ordersByStatus, setOrdersByStatus] = useState({});
  const [riders, setRiders] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeOrder, setActiveOrder] = useState(null);
  const [assignTargets, setAssignTargets] = useState([]);
  const [selectedPacked, setSelectedPacked] = useState([]);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setLoading(true);
      setError('');
    }
    try {
      const [columns, ridersList, summaryData] = await Promise.all([
        Promise.all(ORDER_QUEUE_COLUMNS.map((col) => listOrders(col.key, user.idToken))),
        listRiders(user.idToken),
        getOrdersSummary(user.idToken),
      ]);
      const ridersMap = Object.fromEntries(ridersList.map((r) => [r.id, r]));
      const byStatus = {};
      ORDER_QUEUE_COLUMNS.forEach((col, i) => {
        byStatus[col.key] = sortByUrgency(columns[i]).map((o) => ({
          ...o,
          rider: o.deliveryPersonSub ? (ridersMap[o.deliveryPersonSub] || { id: o.deliveryPersonSub, name: o.deliveryPersonSub, phone: null }) : null,
        }));
      });
      setOrdersByStatus(byStatus);
      setRiders(ridersList);
      setSummary(summaryData);
    } catch (err) {
      if (silent) {
        notify(friendlyError(err));
      } else {
        setError(err.message || 'Could not load the order queue.');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [user.idToken, notify]);

  useEffect(() => {
    load();
  }, [load]);

  const refresh = useCallback(() => load({ silent: true }), [load]);

  const allOrders = useMemo(() => Object.values(ordersByStatus).flat(), [ordersByStatus]);

  const riderTripCounts = useMemo(() => {
    const counts = {};
    allOrders.forEach((o) => {
      if (o.status === ORDER_STATUS.OUT_FOR_DELIVERY && o.rider) {
        counts[o.rider.id] = (counts[o.rider.id] || 0) + 1;
      }
    });
    return counts;
  }, [allOrders]);

  const toggleSelectPacked = (orderId) => {
    setSelectedPacked((prev) => {
      if (prev.includes(orderId)) return prev.filter((id) => id !== orderId);
      if (prev.length >= MAX_TRIP_ORDERS) return prev;
      return [...prev, orderId];
    });
  };

  const openBatchAssign = () => {
    const targets = (ordersByStatus[ORDER_STATUS.PACKED] || []).filter((o) => selectedPacked.includes(o.id));
    setAssignTargets(targets);
  };

  const closeAssignDrawer = () => {
    setAssignTargets([]);
    setSelectedPacked([]);
  };

  const withBusy = async (id, action, successMessage) => {
    setBusyId(id);
    try {
      await action();
      if (successMessage) notify(successMessage);
      await refresh();
      refreshQueueBadge();
    } catch (err) {
      notify(friendlyError(err));
    } finally {
      setBusyId(null);
    }
  };

  const handleConfirm = (id) => withBusy(id, () => confirmOrderApi(id, user.idToken), `Order ${id} confirmed`);
  const handleCancel = (id) => withBusy(id, () => cancelOrderApi(id, undefined, user.idToken), `Order ${id} cancelled`);
  const handlePack = (id) => withBusy(id, () => packOrderApi(id, user.idToken), `Order ${id} marked packed`);
  const handleUnassign = (id) => withBusy(id, () => unassignRiderApi(id, user.idToken), `Rider unassigned from ${id}`);
  // const handleResend = (id) => withBusy(id, () => resendBillApi(id, user.idToken), `Invoice re-sent for ${id}`);

  const handleAssign = async (orderIds, riderId) => {
    const rider = riders.find((r) => r.id === riderId);
    setBusyId(orderIds[0]);
    try {
      await Promise.all(orderIds.map((id) => assignRiderApi(id, riderId, user.idToken)));
      notify(
        orderIds.length > 1
          ? `${rider?.name || 'Rider'} assigned to ${orderIds.length} orders (${orderIds.join(', ')})`
          : `${rider?.name || 'Rider'} assigned to ${orderIds[0]}`,
      );
      await refresh();
    } catch (err) {
      notify(friendlyError(err));
    } finally {
      setBusyId(null);
    }
  };

  const stats = useMemo(() => {
    if (!summary) return [];
    const total = Object.values(summary.counts).reduce((sum, n) => sum + n, 0);
    return [
      { key: 'total', icon: ClipboardList, label: 'Total orders', value: total, tint: '#F2B922' },
      { key: 'awaiting', icon: PackageCheck, label: 'Awaiting confirmation', value: summary.awaitingConfirmation, tint: '#2F6FED' },
      { key: 'transit', icon: Truck, label: 'Out for delivery', value: summary.outForDelivery, tint: '#0D9488' },
      { key: 'delivered', icon: CheckCircle2, label: 'Delivered', value: summary.delivered, tint: '#2E7D32' },
    ];
  }, [summary]);

  return (
    <div>
      {stats.length > 0 && (
        <div className={styles.statGrid}>
          {stats.map((s) => (
            <StatCard key={s.key} icon={s.icon} label={s.label} value={s.value} tint={s.tint} />
          ))}
        </div>
      )}

      {loading && <p className={styles.loadingText}>Loading order queue…</p>}

      {!loading && error && (
        <EmptyState
          icon={AlertTriangle}
          title="Could not load the order queue"
          description={error}
          action={<Button variant="outline" onClick={() => load()}>Retry</Button>}
        />
      )}

      {!loading && !error && (
        <div className={styles.board}>
          {ORDER_QUEUE_COLUMNS.map((col) => {
            const columnOrders = ordersByStatus[col.key] || [];
            const accent = statusColors[col.key]?.fg;
            const isPackedColumn = col.key === ORDER_STATUS.PACKED;
            return (
              <section className={styles.column} key={col.key}>
                <header className={styles.columnHeader}>
                  <div className={styles.columnTitleRow}>
                    <span className={styles.columnDot} style={{ background: accent }} />
                    <h3 className={styles.columnTitle}>{col.title}</h3>
                    <span className={styles.columnCount}>{columnOrders.length}</span>
                  </div>
                  <p className={styles.columnHint}>{col.hint}</p>
                </header>

                {isPackedColumn && selectedPacked.length > 0 && (
                  <div className={styles.batchBar}>
                    <span className={styles.batchCount}>
                      {selectedPacked.length} of {MAX_TRIP_ORDERS} selected for one trip
                    </span>
                    <div className={styles.batchActions}>
                      <button className={styles.batchClear} onClick={() => setSelectedPacked([])} aria-label="Clear selection">
                        <X size={13} />
                      </button>
                      <Button size="sm" variant="primary" icon={Bike} onClick={openBatchAssign}>
                        Assign together
                      </Button>
                    </div>
                  </div>
                )}

                <div className={styles.columnBody}>
                  {columnOrders.length === 0 ? (
                    <EmptyState title="All clear" description="No orders here right now." />
                  ) : (
                    columnOrders.map((order) => (
                      <OrderCard
                        key={order.id}
                        order={order}
                        onOpen={setActiveOrder}
                        onConfirm={handleConfirm}
                        onCancel={handleCancel}
                        onPack={handlePack}
                        onAssign={(o) => setAssignTargets([o])}
                        onUnassign={handleUnassign}
                        tripSize={order.rider ? riderTripCounts[order.rider.id] : 0}
                        selected={isPackedColumn ? selectedPacked.includes(order.id) : undefined}
                        selectDisabled={isPackedColumn ? selectedPacked.length >= MAX_TRIP_ORDERS : undefined}
                        onToggleSelect={isPackedColumn ? toggleSelectPacked : undefined}
                        busy={busyId === order.id}
                      />
                    ))
                  )}
                </div>
              </section>
            );
          })}
        </div>
      )}

      {/* Resend invoice is intentionally disabled in the Order Queue. */}
      <OrderDetailsDrawer
        order={activeOrder}
        onClose={() => setActiveOrder(null)}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        onPack={handlePack}
        onAssign={(order) => { setActiveOrder(null); setAssignTargets([order]); }}
        onUnassign={handleUnassign}
      />

      <AssignRiderDrawer
        orders={assignTargets}
        riders={riders}
        activeRideCounts={riderTripCounts}
        onClose={closeAssignDrawer}
        onAssign={handleAssign}
      />
    </div>
  );
}
